import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { Feature, GeoJsonProperties, Geometry } from 'geojson';

import MapaImpactoVisor, {
  EscenarioMapa,
  ImpactoMapaPayload
} from './MapaImpactoVisor';
import { SimpleObject } from '../../types';
import { getUncatchEndpointData } from '../../utils/request';

type EscenarioPayload = {
  snapshot_dmin?: ImpactoMapaPayload;
  snapshot_dmax?: ImpactoMapaPayload;
  dia_verano?: ImpactoMapaPayload;
  dia_invierno?: ImpactoMapaPayload;
};

type MitigacionEntry = {
  mitigacionId: number;
  tipoMitigacion?: string;
  orden?: number;
  datos: EscenarioPayload;
};

type ImpactoMapaMitigacionesResponse = {
  proyectoSinMitigacion?: EscenarioPayload | null;
  mitigaciones?: MitigacionEntry[];
} | null;

type CasoMapa =
  | { tipo: 'sinMitigacion' }
  | { tipo: 'mitigacion'; mitigacionId: number };

type Props = {
  estudioId: string | number;
  /** Lista de mitigaciones tal como la entrega
   *  `GET /api/estudios-conexion/:id/mitigaciones` (incluye `id`, `tipoMitigacion`,
   *  `orden`, etc.). El componente usa esa lista para etiquetar el selector y
   *  como contrato de ordenamiento. */
  mitigaciones: SimpleObject[];
  /** Estado del mapa (features ya cargadas) compartido con el bloque
   *  "Ubicación del Proyecto"; reutilizamos para no duplicar fetch. */
  mapBaseState: {
    mapPointFeatures: Feature<Geometry, GeoJsonProperties>[];
    mapLineFeatures: Feature<Geometry, GeoJsonProperties>[];
    mapPolygonFeatures: Feature<Geometry, GeoJsonProperties>[];
    electricFeeders: SimpleObject;
    mapCenter: { lat: number; lng: number };
  };
};

const ETIQUETA_ESCENARIO: Record<EscenarioMapa, string> = {
  snapshot_dmin: 'Snapshot Dmin',
  snapshot_dmax: 'Snapshot Dmax',
  dia_verano: 'Día Verano',
  dia_invierno: 'Día Invierno'
};

const VALOR_SIN_MITIGACION = '__sin_mitigacion__';

function etiquetaMitigacion(m: SimpleObject): string {
  const tipo = String(m.tipoMitigacion || m.tipo_mitigacion || '');
  const orden = m.orden != null ? Number(m.orden) + 1 : '';
  return `${tipo.replace(/_/g, ' ')}${orden ? ` (#${orden})` : ''}`;
}

/** Bloque de mapa de impacto para Fase 2 (NORM-037). El primer selector
 *  permite alternar entre el caso "con proyecto sin mitigación" (igual a Fase
 *  1) y cada mitigación; el segundo selector elige escenario; el tercero
 *  (dentro del visor) elige capa. Una sola request al endpoint
 *  `/impacto-mapa-mitigaciones` alimenta todos los casos. */
const MapaImpactoMitigacion: FunctionComponent<Props> = ({
  estudioId,
  mitigaciones,
  mapBaseState
}) => {
  const [data, setData] = useState<ImpactoMapaMitigacionesResponse>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [caso, setCaso] = useState<CasoMapa>({ tipo: 'sinMitigacion' });
  const [escenarioMapa, setEscenarioMapa] = useState<EscenarioMapa>('snapshot_dmin');

  useEffect(() => {
    let cancelled = false;
    const fetchImpacto = async () => {
      setLoading(true);
      setError(null);
      try {
        const resp = await getUncatchEndpointData({
          endpoint: `estudios-conexion/${estudioId}/impacto-mapa-mitigaciones`
        });
        if (!cancelled) {
          setData(resp || null);
        }
      } catch (err) {
        if (!cancelled) {
          setError('No se pudo cargar el mapa de impacto.');
          setData(null);
        }
      }
      if (!cancelled) setLoading(false);
    };
    fetchImpacto();
    return () => {
      cancelled = true;
    };
  }, [estudioId]);

  const payload = useMemo<ImpactoMapaPayload | null>(() => {
    if (!data) return null;
    if (caso.tipo === 'sinMitigacion') {
      return data.proyectoSinMitigacion?.[escenarioMapa] ?? null;
    }
    const entry = (data.mitigaciones || []).find((m) => m.mitigacionId === caso.mitigacionId);
    if (!entry) return null;
    return entry.datos?.[escenarioMapa] ?? null;
  }, [data, caso, escenarioMapa]);

  const tituloCaso = useMemo<string>(() => {
    if (caso.tipo === 'sinMitigacion') return 'Caso con proyecto sin mitigación';
    const m = mitigaciones.find((mm) => Number(mm.id) === caso.mitigacionId);
    return m ? etiquetaMitigacion(m) : 'Mitigación';
  }, [caso, mitigaciones]);

  const onCasoChange = (value: string) => {
    if (value === VALOR_SIN_MITIGACION) {
      setCaso({ tipo: 'sinMitigacion' });
    } else {
      setCaso({ tipo: 'mitigacion', mitigacionId: Number(value) });
    }
  };

  const valorCaso =
    caso.tipo === 'sinMitigacion' ? VALOR_SIN_MITIGACION : String(caso.mitigacionId);

  const hint = (
    <>
      El mapa resalta los elementos cuyo problema <strong>persiste o aparece</strong> respecto del{' '}
      caso <strong>sin proyecto</strong>, evaluando dentro de{' '}
      <strong>{tituloCaso}</strong> y escenario{' '}
      <strong>{ETIQUETA_ESCENARIO[escenarioMapa]}</strong>. Seleccione qué capa visualizar.
    </>
  );

  return (
    <div className='ecg-mapa-impacto-mit-container'>
      <div className='ecg-mapa-impacto-mit-selectors'>
        <div>
          <label htmlFor='mapa-mitigacion-caso' className='ecg-label'>
            Caso:
          </label>
          <select
            id='mapa-mitigacion-caso'
            className='ecg-input'
            value={valorCaso}
            onChange={(e) => onCasoChange(e.target.value)}>
            <option value={VALOR_SIN_MITIGACION}>Caso con proyecto sin mitigación</option>
            {mitigaciones.map((m) => (
              <option key={String(m.id)} value={String(m.id)}>
                {etiquetaMitigacion(m)}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor='mapa-mitigacion-escenario' className='ecg-label'>
            Escenario:
          </label>
          <select
            id='mapa-mitigacion-escenario'
            className='ecg-input'
            value={escenarioMapa}
            onChange={(e) => setEscenarioMapa(e.target.value as EscenarioMapa)}>
            <option value='snapshot_dmin'>Snapshot Dmin</option>
            <option value='snapshot_dmax'>Snapshot Dmax</option>
            <option value='dia_verano'>Día Verano</option>
            <option value='dia_invierno'>Día Invierno</option>
          </select>
        </div>
      </div>

      <MapaImpactoVisor
        payload={payload}
        mapBaseState={mapBaseState}
        loading={loading}
        error={error}
        hint={hint}
      />
    </div>
  );
};

export default MapaImpactoMitigacion;
